import { useEffect, useState } from "react";
import type { Customer } from "../services/customersApi";
import type { Order } from "@/features/production/services/ordersApi";
import { usePagedOrdersByCustomerId } from "@/features/production/hooks/useOrders";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import { TablePagination } from "@/shared/components/TablePagination";

interface CustomerOrderHistoryDialogProps {
  customer: Customer | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    minimumFractionDigits: 2,
  }).format(value);

const formatDateOrDash = (value?: string) =>
  value ? new Date(value).toLocaleDateString() : "-";

const getBalanceAmount = (order: Order) => {
  const total = Number(order.totalAmount ?? 0);
  const paid = Number(order.paidAmount ?? 0);
  const computed = total - paid;
  const balance = Number(order.balanceAmount ?? computed);
  return Math.max(balance, 0);
};

const STATUS_VARIANTS: Record<string, "outline" | "secondary" | "default" | "destructive"> = {
  Placed: "outline",
  Confirmed: "outline",
  Processing: "secondary",
  SowingInitiated: "secondary",
  Growing: "default",
  ReadyForDelivery: "default",
  Dispatched: "default",
  PartiallyDispatched: "secondary",
  Delivered: "secondary",
  Cancelled: "destructive",
  WalkInSale: "default",
};

export const CustomerOrderHistoryDialog = ({
  customer,
  open,
  onOpenChange,
}: CustomerOrderHistoryDialogProps) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Reset paging whenever the targeted customer changes so the table starts at page 1.
  useEffect(() => {
    setPageNumber(1);
  }, [customer?.id]);

  const { data, isLoading, isFetching, error } = usePagedOrdersByCustomerId({
    customerId: customer?.id ?? "",
    pageNumber,
    pageSize,
  });

  const orders = data?.items ?? [];

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPageNumber(1);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[85vh] overflow-y-auto"
        style={{ width: "80vw", maxWidth: "1200px" }}
      >
        <DialogHeader>
          <DialogTitle>
            Order History{customer ? ` — ${customer.customerName}` : ""}
          </DialogTitle>
          <DialogDescription>
            All orders placed by this customer, newest first.
          </DialogDescription>
        </DialogHeader>

        {isLoading && !data && (
          <div className="py-12">
            <LoadingAnimation />
          </div>
        )}

        {error && !isLoading && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-red-600">Failed to load order history</p>
          </div>
        )}

        {!isLoading && !error && orders.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No orders found for this customer
          </div>
        )}

        {!error && orders.length > 0 && (
          <div className="space-y-4">
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order #</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Expected Delivery</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => {
                    const balance = getBalanceAmount(order);
                    return (
                      <TableRow key={order.id}>
                        <TableCell className="font-mono text-sm">
                          {order.orderNumber}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDateOrDash(order.createdAt)}
                        </TableCell>
                        <TableCell className="text-sm">
                          <ul className="space-y-0.5">
                            {order.orderItems.map((item) => {
                              const label = [item.cropType, item.variety]
                                .filter(Boolean)
                                .join(" — ") || item.productName;
                              return (
                                <li key={item.id} className="whitespace-nowrap">
                                  <span className="font-medium">{label}</span>
                                  <span className="text-muted-foreground"> × {item.quantity}</span>
                                </li>
                              );
                            })}
                          </ul>
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">
                          {formatCurrency(Number(order.totalAmount ?? 0))}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">
                          <span
                            className={
                              balance > 0 ? "text-amber-700" : "text-emerald-700"
                            }
                          >
                            {formatCurrency(balance)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={balance > 0 ? "outline" : "secondary"}>
                            {order.paymentStatus || (balance > 0 ? "Pending" : "Paid")}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANTS[order.status] ?? "outline"}>
                            {order.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDateOrDash(order.expectedDeliveryDate)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <TablePagination
              pageNumber={data?.currentPage ?? pageNumber}
              pageSize={data?.pageSize ?? pageSize}
              totalCount={data?.totalCount ?? 0}
              totalPages={data?.totalPages ?? 0}
              onPageChange={setPageNumber}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[5, 10, 25, 50]}
              isFetching={isFetching}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
